import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import {
  type MCP,
  MCPSchema,
  type MCPClass,
  MCPClassSchema,
} from '@/proto/agentplatform/v1/mcp_pb';
import {
  ListMCPClassesResponseSchema,
  CreateMCPClassRequestSchema,
  CreateMCPClassResponseSchema,
  GetMCPClassRequestSchema,
  GetMCPClassResponseSchema,
  UpdateMCPClassRequestSchema,
  UpdateMCPClassResponseSchema,
  DeleteMCPClassRequestSchema,
  DeleteMCPClassResponseSchema,
  CreateMCPInstanceFromClassRequestSchema,
  CreateMCPInstanceFromClassResponseSchema,
  UpdateMCPInstanceFromClassRequestSchema,
  UpdateMCPInstanceFromClassResponseSchema,
} from '@/proto/agentplatform/httpv2/mcp_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface MCPClassListResult {
  data: MCPClass[];
  total: number;
}

/** 由服务类派生实例时的入参（v2 已扁平化为 name/description/params）。 */
export interface MCPInstanceFromClassInput {
  name: string;
  description: string;
  params: Record<string, string>;
}

export const mcpClassApi = {
  listMCPClasses: async (params: ListRequest): Promise<MCPClassListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListMCPClasses',
      toJson(ListRequestSchema, params)
    );
    const resp = fromJson(ListMCPClassesResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getMCPClass: async (id: string): Promise<MCPClass> => {
    const req = create(GetMCPClassRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetMCPClass',
      toJson(GetMCPClassRequestSchema, req)
    );
    const resp = fromJson(GetMCPClassResponseSchema, responseObject.data);
    return resp.data ?? create(MCPClassSchema, {});
  },

  createMCPClass: async (data: MCPClass): Promise<MCPClass> => {
    const req = create(CreateMCPClassRequestSchema, { mcpClass: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateMCPClass',
      toJson(CreateMCPClassRequestSchema, req)
    );
    const resp = fromJson(CreateMCPClassResponseSchema, responseObject.data);
    return resp.data ?? create(MCPClassSchema, {});
  },

  updateMCPClass: async (id: string, data: MCPClass): Promise<MCPClass> => {
    const req = create(UpdateMCPClassRequestSchema, { id, mcpClass: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateMCPClass',
      toJson(UpdateMCPClassRequestSchema, req)
    );
    const resp = fromJson(UpdateMCPClassResponseSchema, responseObject.data);
    return resp.data ?? create(MCPClassSchema, {});
  },

  deleteMCPClass: async (id: string): Promise<void> => {
    const req = create(DeleteMCPClassRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteMCPClass',
      toJson(DeleteMCPClassRequestSchema, req)
    );
    fromJson(DeleteMCPClassResponseSchema, responseObject.data);
  },

  createMCPInstanceFromClass: async (
    classId: string,
    data: MCPInstanceFromClassInput
  ): Promise<MCP> => {
    const req = create(CreateMCPInstanceFromClassRequestSchema, {
      classId,
      name: data.name,
      description: data.description,
      params: data.params,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateMCPInstanceFromClass',
      toJson(CreateMCPInstanceFromClassRequestSchema, req)
    );
    const resp = fromJson(CreateMCPInstanceFromClassResponseSchema, responseObject.data);
    return resp.data ?? create(MCPSchema, {});
  },

  updateMCPInstanceFromClass: async (
    id: string,
    classId: string,
    data: MCPInstanceFromClassInput
  ): Promise<MCP> => {
    const req = create(UpdateMCPInstanceFromClassRequestSchema, {
      id,
      classId,
      name: data.name,
      description: data.description,
      params: data.params,
    });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateMCPInstanceFromClass',
      toJson(UpdateMCPInstanceFromClassRequestSchema, req)
    );
    const resp = fromJson(UpdateMCPInstanceFromClassResponseSchema, responseObject.data);
    return resp.data ?? create(MCPSchema, {});
  },
};
